/* ==============================================================================
   Menus de servidor
   ==============================================================================
   Três coisas pedidas que não existiam, reunidas num arquivo porque compartilham
   a mesma mecânica de painel flutuante:

     1. "falta o botão de ingressar em um servidor e digitar o código"
     2. "gerar um código de compartilhamento"
     3. "opções com o botão direito nos servidores para mudar logo, banner e
         configurações"

   Por que um painel próprio e não `useDialog`
   ===========================================
   O `useDialog` resolve pergunta de uma linha e confirmação. Estes menus têm
   várias ações, prévia de imagem e um código para copiar — e "copiar" precisa
   ficar visível depois do clique para a pessoa saber que copiou. Empilhar isso
   em `ask` produziria uma sequência de três diálogos para uma tarefa.

   O que TODO menu flutuante daqui precisa acertar
   ===============================================
     · fechar no Escape e no clique fora;
     · devolver o foco a quem o abriu (senão o teclado volta para o topo da
       página, e quem navega por teclado se perde);
     · não sair da viewport (um menu de contexto aberto perto da borda inferior
       fica com metade cortada, e é onde a dock fica);
     · `role="menu"` com `aria-orientation`, porque um `div` com cliques não é
       navegável por leitor de tela.
   ============================================================================== */

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/* ==============================================================================
   Casca flutuante reaproveitada
============================================================================== */

interface FloatingPanelProps {
  /** Âncora em coordenadas de viewport. */
  at: { x: number; y: number };
  onClose: () => void;
  children: ReactNode;
  width?: number;
  label: string;
}

function FloatingPanel({ at, onClose, children, width = 248, label }: FloatingPanelProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ left: at.x, top: at.y });

  /*
   * A posição é corrigida DEPOIS da montagem, com a altura real.
   *
   * `useLayoutEffect` e não `useEffect`: o navegador não deve pintar o menu na
   * posição errada e corrigir no quadro seguinte — isso é um salto visível.
   * O layout effect roda antes da pintura.
   */
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const margin = 8;

    let left = at.x;
    let top = at.y;

    // Passa da direita: alinha pela borda direita do menu em vez de cortar.
    if (left + rect.width + margin > window.innerWidth) {
      left = Math.max(margin, window.innerWidth - rect.width - margin);
    }
    /*
     * Passa de baixo: abre para CIMA da âncora.
     *
     * Só empurrar para cima até caber deixaria o menu cobrindo o botão que o
     * abriu. Abrir na direção oposta é o comportamento que todo menu de contexto
     * de sistema operacional tem, e é o que a dock exige — ela vive na base da
     * tela, então quase todo menu de servidor abre para cima.
     */
    if (top + rect.height + margin > window.innerHeight) {
      top = Math.max(margin, at.y - rect.height);
    }

    setPosition({ left, top });
  }, [at.x, at.y]);

  /* ---- Escape, clique fora, e devolução do foco ---- */
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    const onPointerDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) onClose();
    };

    document.addEventListener('keydown', onKeyDown, true);
    /*
     * `pointerdown` e não `click`.
     *
     * Com `click`, apertar o botão fora e soltar dentro do menu conta como
     * clique dentro e o menu não fecha. Com `pointerdown` a decisão é tomada no
     * aperto, que é o que a pessoa percebe como "cliquei fora".
     */
    document.addEventListener('pointerdown', onPointerDown, true);

    // Foco no primeiro item: menu aberto sem foco não é navegável por teclado.
    ref.current?.querySelector<HTMLElement>('[data-menu-item]')?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
      // `isConnected` porque o abridor pode ter sido desmontado junto.
      if (opener?.isConnected) opener.focus();
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="menu"
      aria-label={label}
      aria-orientation="vertical"
      className="fixed overflow-hidden"
      style={{
        left: position.left,
        top: position.top,
        width,
        zIndex: 'var(--z-popover)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--color-surface-overlay)',
        backdropFilter: 'blur(20px) saturate(160%)',
        boxShadow:
          'var(--glass-edge), 0 20px 56px oklch(0% 0 0 / 0.5), var(--glow-medium)',
      }}
    >
      {children}
    </div>
  );
}

/* ==============================================================================
   Item de menu
============================================================================== */

function MenuItem({
  label,
  hint,
  icon,
  onClick,
  danger = false,
  disabled = false,
}: {
  label: string;
  /*
   * `| undefined` explicito em toda prop opcional.
   *
   * Com `exactOptionalPropertyTypes`, `hint?: string` significa "ausente OU
   * string" e NAO aceita `undefined` explicito. Um chamador que faca
   * `hint={condicao ? texto : undefined}` -- que e a forma natural de escrever
   * isso -- e recusado sem a uniao.
   */
  hint?: string | undefined;
  icon: ReactNode;
  onClick: () => void;
  danger?: boolean | undefined;
  disabled?: boolean | undefined;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      data-menu-item
      disabled={disabled}
      onClick={onClick}
      className="flex w-full items-start gap-2.5 px-3 py-2 text-left"
      style={{
        color: disabled
          ? 'var(--color-text-disabled)'
          : danger
            ? 'var(--color-danger-on-subtle)'
            : 'var(--color-text-secondary)',
        fontSize: 'var(--text-xs)',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      onMouseEnter={(event) => {
        if (!disabled) {
          event.currentTarget.style.background = danger
            ? 'var(--color-danger-subtle)'
            : 'var(--color-accent-subtle)';
        }
      }}
      onMouseLeave={(event) => {
        event.currentTarget.style.background = 'transparent';
      }}
      onFocus={(event) => {
        if (!disabled) {
          event.currentTarget.style.background = danger
            ? 'var(--color-danger-subtle)'
            : 'var(--color-accent-subtle)';
        }
      }}
      onBlur={(event) => {
        event.currentTarget.style.background = 'transparent';
      }}
    >
      <span aria-hidden="true" className="mt-0.5 shrink-0 opacity-70">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-medium">{label}</span>
        {hint !== undefined && (
          <span
            className="mt-0.5 block"
            style={{
              fontSize: 'var(--text-2xs)',
              color: 'var(--color-text-tertiary)',
              lineHeight: 'var(--leading-snug)',
            }}
          >
            {hint}
          </span>
        )}
      </span>
    </button>
  );
}

function Divider() {
  return (
    <div
      aria-hidden="true"
      style={{ height: 1, background: 'var(--color-border-subtle)' }}
    />
  );
}

/* ==============================================================================
   Ícones — 14px, traço de 1.5, para casar com o resto da interface
============================================================================== */

const ic = {
  image: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <circle cx="8.5" cy="9.5" r="1.6" />
      <path d="M21 16l-5-5-7 7" strokeLinecap="round" />
    </svg>
  ),
  banner: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="2.5" y="7" width="19" height="10" rx="2" />
      <path d="M2.5 12h19" strokeDasharray="2 2" />
    </svg>
  ),
  gear: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.3 5.3l2.1 2.1M16.6 16.6l2.1 2.1M18.7 5.3l-2.1 2.1M7.4 16.6l-2.1 2.1" strokeLinecap="round" />
    </svg>
  ),
  link: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M9.5 14.5l5-5" strokeLinecap="round" />
      <path d="M13 7l1.8-1.8a3.8 3.8 0 015.4 5.4L18.4 12" strokeLinecap="round" />
      <path d="M11 17l-1.8 1.8a3.8 3.8 0 01-5.4-5.4L5.6 12" strokeLinecap="round" />
    </svg>
  ),
  channel: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M9 3.5L7 20.5M17 3.5l-2 17M4 8.5h16M3 15.5h16" strokeLinecap="round" />
    </svg>
  ),
  exit: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M15 4.5H6.5A2 2 0 004.5 6.5v11a2 2 0 002 2H15" strokeLinecap="round" />
      <path d="M18.5 12H10M15.5 8.5l3.5 3.5-3.5 3.5" strokeLinecap="round" />
    </svg>
  ),
  plus: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M12 5.5v13M5.5 12h13" strokeLinecap="round" />
    </svg>
  ),
  key: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="8" cy="12" r="3.5" />
      <path d="M11.5 12h9M17.5 12v3M20.5 12v2.5" strokeLinecap="round" />
    </svg>
  ),
  chat: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M4 5.5h16v10H9l-5 4v-14z" strokeLinejoin="round" />
    </svg>
  ),
  phone: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M6.5 3.5l3 1 1 3.5-2 1.5a11 11 0 005 5l1.5-2 3.5 1 1 3c-6 1.5-13-5.5-13-13z"
            strokeLinejoin="round" />
    </svg>
  ),
  bell: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M7 10a5 5 0 0110 0v4l1.5 2.5h-13L7 14v-4z" strokeLinejoin="round" />
      <path d="M10 19.5a2 2 0 004 0" strokeLinecap="round" />
    </svg>
  ),
  bellOff: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M7 10a5 5 0 0110 0v4l1.5 2.5h-13L7 14v-4z" strokeLinejoin="round" />
      <path d="M4 4l16 16" strokeLinecap="round" />
    </svg>
  ),
  userMinus: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="9.5" cy="8" r="3.2" />
      <path d="M3.5 19.5c0-3.3 2.7-5.2 6-5.2 1.2 0 2.3.25 3.2.7" strokeLinecap="round" />
      <path d="M15.5 17h5" strokeLinecap="round" />
    </svg>
  ),
  ban: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="12" cy="12" r="8.2" />
      <path d="M6.2 6.2l11.6 11.6" strokeLinecap="round" />
    </svg>
  ),
};

/* ==============================================================================
   1. Menu de contexto de um servidor
============================================================================== */

export interface GuildContextMenuProps {
  at: { x: number; y: number };
  guildName: string;
  /** Falso esconde as ações de imagem e configuração. */
  canManage: boolean;
  canCreateInvite: boolean;
  canManageChannels: boolean;
  isOwner: boolean;
  onClose: () => void;
  onChangeIcon: () => void;
  onChangeBanner: () => void;
  onOpenSettings: () => void;
  onCreateInvite: () => void;
  onCreateChannel: (type: 'text' | 'voice') => void;
  onLeave: () => void;
}

export function GuildContextMenu({
  at,
  guildName,
  canManage,
  canCreateInvite,
  canManageChannels,
  isOwner,
  onClose,
  onChangeIcon,
  onChangeBanner,
  onOpenSettings,
  onCreateInvite,
  onCreateChannel,
  onLeave,
}: GuildContextMenuProps) {
  const run = (action: () => void) => () => {
    onClose();
    action();
  };

  return (
    <FloatingPanel at={at} onClose={onClose} label={`Opções de ${guildName}`} width={252}>
      <div
        className="truncate px-3 pb-1.5 pt-2.5 font-semibold"
        style={{
          fontSize: 'var(--text-2xs)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--color-text-tertiary)',
        }}
      >
        {guildName}
      </div>

      {canCreateInvite && (
        <MenuItem
          icon={ic.link}
          label="Gerar código de convite"
          hint="Cria um link para compartilhar"
          onClick={run(onCreateInvite)}
        />
      )}

      {canManageChannels && (
        <>
          <Divider />
          <MenuItem
            icon={ic.channel}
            label="Criar canal de texto"
            onClick={run(() => onCreateChannel('text'))}
          />
          <MenuItem
            icon={ic.plus}
            label="Criar sala de voz"
            onClick={run(() => onCreateChannel('voice'))}
          />
        </>
      )}

      {canManage && (
        <>
          <Divider />
          <MenuItem
            icon={ic.image}
            label="Mudar ícone"
            hint="PNG, JPEG, WebP ou GIF · até 512 KB"
            onClick={run(onChangeIcon)}
          />
          <MenuItem
            icon={ic.banner}
            label="Mudar banner"
            onClick={run(onChangeBanner)}
          />
          <MenuItem
            icon={ic.gear}
            label="Configurações do servidor"
            hint="Cargos, permissões, membros, canais"
            onClick={run(onOpenSettings)}
          />
        </>
      )}

      <Divider />
      <MenuItem
        icon={ic.exit}
        label="Sair do servidor"
        /*
         * O dono não pode sair, e o menu diz por quê em vez de só desabilitar.
         *
         * Um item cinza sem explicação leva a pessoa a clicar várias vezes
         * achando que travou.
         */
        hint={isOwner ? 'O dono não pode sair sem transferir o servidor' : undefined}
        danger
        disabled={isOwner}
        onClick={run(onLeave)}
      />
    </FloatingPanel>
  );
}

/* ==============================================================================
   2. Criar servidor ou entrar com código
============================================================================== */

export interface AddServerMenuProps {
  at: { x: number; y: number };
  onClose: () => void;
  onCreate: () => void;
  onJoin: () => void;
}

export function AddServerMenu({ at, onClose, onCreate, onJoin }: AddServerMenuProps) {
  const run = (action: () => void) => () => {
    onClose();
    action();
  };

  return (
    <FloatingPanel at={at} onClose={onClose} label="Adicionar servidor" width={260}>
      <MenuItem
        icon={ic.plus}
        label="Criar um servidor"
        hint="Você entra como dono, com um canal de texto e uma sala de voz"
        onClick={run(onCreate)}
      />
      <Divider />
      <MenuItem
        icon={ic.key}
        label="Entrar com um código"
        hint="Cole o código ou o link de convite que você recebeu"
        onClick={run(onJoin)}
      />
    </FloatingPanel>
  );
}

/* ==============================================================================
   3. Painel de código de convite gerado
============================================================================== */

export interface InviteCodePanelProps {
  url: string;
  code: string;
  expiresAt: string | null;
  maxUses: number | null;
  onClose: () => void;
}

export function InviteCodePanel({
  url,
  code,
  expiresAt,
  maxUses,
  onClose,
}: InviteCodePanelProps) {
  const [copied, setCopied] = useState<'url' | 'code' | null>(null);
  const titleId = useId();

  const copy = useCallback(async (text: string, what: 'url' | 'code') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      /*
       * O aviso de "copiado" desaparece em 2 s.
       *
       * Permanente, ele mentiria: continuaria dizendo "copiado" depois de a
       * pessoa copiar outra coisa em outro programa.
       */
      setTimeout(() => setCopied(null), 2000);
    } catch {
      /*
       * `navigator.clipboard` exige contexto seguro e pode ser negado.
       *
       * Em vez de mostrar erro, o texto fica selecionável nos dois campos —
       * `Ctrl+C` sempre funciona. É por isso que eles são `<input readOnly>` e
       * não `<span>`.
       */
      setCopied(null);
    }
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const field = (
    value: string,
    what: 'url' | 'code',
    label: string,
    mono: boolean,
  ) => (
    <div>
      <label
        className="mb-1 block font-semibold"
        style={{
          fontSize: 'var(--text-2xs)',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: 'var(--color-text-tertiary)',
        }}
      >
        {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          readOnly
          value={value}
          onFocus={(event) => event.currentTarget.select()}
          className="min-w-0 flex-1 px-2.5 py-2"
          style={{
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-surface-sunken)',
            color: 'var(--color-text-primary)',
            fontSize: mono ? 'var(--text-sm)' : 'var(--text-2xs)',
            fontFamily: mono ? 'var(--font-mono)' : 'inherit',
            letterSpacing: mono ? '0.06em' : 'normal',
            border: '1px solid var(--color-border-subtle)',
          }}
        />
        <button
          type="button"
          onClick={() => void copy(value, what)}
          className="shrink-0 px-2.5 py-2 font-semibold"
          style={{
            borderRadius: 'var(--radius-md)',
            background:
              copied === what ? 'var(--color-success-subtle)' : 'var(--color-accent-subtle)',
            color:
              copied === what
                ? 'var(--color-success-on-subtle)'
                : 'var(--color-accent-on-subtle)',
            fontSize: 'var(--text-2xs)',
            whiteSpace: 'nowrap',
          }}
        >
          {copied === what ? 'Copiado' : 'Copiar'}
        </button>
      </div>
    </div>
  );

  return (
    <div
      className="fixed inset-0 grid place-items-center px-4"
      style={{ zIndex: 'var(--z-modal)', background: 'oklch(0% 0 0 / 0.55)' }}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-sm p-5"
        style={{
          borderRadius: 'var(--radius-xl)',
          background: 'var(--color-surface-overlay)',
          backdropFilter: 'blur(24px) saturate(160%)',
          boxShadow: 'var(--glass-edge), 0 24px 64px oklch(0% 0 0 / 0.5)',
        }}
      >
        <h2
          id={titleId}
          className="font-bold"
          style={{
            fontSize: 'var(--text-base)',
            color: 'var(--color-text-primary)',
            letterSpacing: 'var(--tracking-tight)',
          }}
        >
          Código de convite criado
        </h2>
        <p
          className="mt-1"
          style={{
            fontSize: 'var(--text-xs)',
            lineHeight: 'var(--leading-relaxed)',
            color: 'var(--color-text-tertiary)',
          }}
        >
          Quem abrir o link vê o nome do servidor antes de entrar.
          {maxUses !== null && ` Vale para ${maxUses} ${maxUses === 1 ? 'pessoa' : 'pessoas'}.`}
          {expiresAt !== null &&
            ` Expira em ${new Date(expiresAt).toLocaleString('pt-BR', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}.`}
        </p>

        <div className="mt-4 flex flex-col gap-3">
          {field(url, 'url', 'Link completo', false)}
          {field(code, 'code', 'Só o código', true)}
        </div>

        <button
          type="button"
          onClick={onClose}
          className="mt-4 w-full px-4 py-2.5 font-semibold"
          style={{
            borderRadius: 'var(--radius-full)',
            background: 'var(--color-accent-solid)',
            color: 'var(--color-on-solid)',
            fontSize: 'var(--text-sm)',
          }}
        >
          Pronto
        </button>
      </div>
    </div>
  );
}

/* ==============================================================================
   4. Menu de contexto de um amigo
   ==============================================================================
   Botão direito no amigo na lateral. Pedido: "opção ao clicar com o botão direito
   no ícone do user do amigo para caso como excluir, silenciar... e assim por
   diante".

   A ordem dos itens é a ordem de FREQUÊNCIA, não de importância
   ==============================================================================
   Mandar mensagem e ligar vêm primeiro porque são o que se faz todo dia.
   Silenciar vem depois de um fio. Remover e bloquear ficam no fim, atrás de outro
   fio, porque são raros e irreversíveis do ponto de vista de quem clica sem
   olhar — e a distância física é a única proteção real contra o clique errado
   num menu que aparece sob o cursor.

   Silenciar e bloquear são coisas DIFERENTES, e o menu diz isso
   ==============================================================================
   Silenciar é local: nada vai ao servidor, e a outra pessoa não sabe. Bloquear
   passa pelo servidor, impede o outro de escrever e de ligar, e é visível no
   comportamento. Um menu que os apresenta como variações do mesmo botão faz
   alguém bloquear achando que estava só abaixando o volume.
============================================================================== */

export interface FriendContextMenuProps {
  at: { x: number; y: number };
  name: string;
  username: string;
  muted: boolean;
  onClose: () => void;
  onOpenConversation: () => void;
  onCall: () => void;
  onToggleMute: () => void;
  onRemove: () => void;
  onBlock: () => void;
}

export function FriendContextMenu({
  at,
  name,
  username,
  muted,
  onClose,
  onOpenConversation,
  onCall,
  onToggleMute,
  onRemove,
  onBlock,
}: FriendContextMenuProps) {
  const run = (action: () => void) => () => {
    onClose();
    action();
  };

  return (
    <FloatingPanel at={at} onClose={onClose} label={`Opções de ${name}`} width={258}>
      <div className="px-3 pb-1.5 pt-2.5">
        <p
          className="truncate font-semibold"
          style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-primary)' }}
        >
          {name}
        </p>
        <p
          className="truncate"
          style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}
        >
          @{username}
        </p>
      </div>

      <Divider />

      <MenuItem
        icon={ic.chat}
        label="Enviar mensagem"
        onClick={run(onOpenConversation)}
      />
      <MenuItem
        icon={ic.phone}
        label="Ligar"
        hint="Conversa de voz só entre vocês dois, com tela"
        onClick={run(onCall)}
      />

      <Divider />

      <MenuItem
        icon={muted ? ic.bell : ic.bellOff}
        label={muted ? 'Reativar notificações' : 'Silenciar notificações'}
        /*
         * O texto diz que é LOCAL.
         *
         * Sem isso, "silenciar" numa lista de pessoas é lido como "silenciar a
         * pessoa" — algo que afeta o outro lado. Silenciar aqui não sai deste
         * navegador.
         */
        hint={muted ? undefined : 'Só para você, neste dispositivo'}
        onClick={run(onToggleMute)}
      />

      <Divider />

      <MenuItem
        icon={ic.userMinus}
        label="Remover amizade"
        hint="O histórico da conversa permanece"
        danger
        onClick={run(onRemove)}
      />
      <MenuItem
        icon={ic.ban}
        label="Bloquear"
        hint="Ele não poderá escrever nem ligar para você"
        danger
        onClick={run(onBlock)}
      />
    </FloatingPanel>
  );
}
